import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import API from '../../Services/API';
import _ from 'lodash';
//
// @params data.type list or single
//
export const LoadOrders = createAsyncThunk('order/load', async (data) => {
  const params = {
    company_id: data.companyId || process.env.REACT_APP_COMP_EMM,
    $filter: data.filter,
    $count: true,
    $skip: (data.currentPage - 1) * data.pageSize,
    $top: data.pageSize
  };
  if (isNaN(params.$skip) || params.$skip < 0) {
    delete params.$skip;
  }
  if (data.skipPaginate === true) {
    delete params.$skip;
    delete params.$top;
  }
  const res = await API().get(`${data.endpoint}`, { params });
  return {
    orders: res.data.value,
    count: res.data['@odata.count'],
    type: data.type
  };
});

export const LoadSearchedOrders = createAsyncThunk('order/search', async (data) => {
  const params = {
    company_id: data.companyId || process.env.REACT_APP_COMP_EMM,
    $filter: data.filter
  };
  const res = await API().get(`${data.endpoint}`, { params });
  return {
    orders: res.data.value
  };
});

export const LoadOrderLines = createAsyncThunk('order/linesload', async (data) => {
  const params = {
    company_id: data.companyId || process.env.REACT_APP_COMP_EMM,
    $filter: data.filter,
    $count: true,
    $skip: 0,
    $top: 0
  };
  if (data.implementPaginate === true) {
    if (data.skipPaginate === false) {
      params.$skip = (data.currentPage - 1) * data.pageSize;
      params.$top = data.pageSize;
    } else {
      delete params.$skip;
      delete params.$top;
    }
  } else {
    delete params.$skip;
    delete params.$top;
  }
  const res = await API().get(`${data.endpoint}`, { params });
  return {
    lines: res.data.value,
    count: res.data['@odata.count'],
    type: data.type
  };
});

export const LoadDashBoardPendingPOs = createAsyncThunk('order/pendingpoload', async (data) => {
  const params = {
    company_id: data.companyId || process.env.REACT_APP_COMP_EMM,
    $filter: data.filter,
    $count: true,
    $top: 0
  };
  const res = await API().get('/purchase_order_header', { params });
  return {
    count: res.data['@odata.count']
  };
});

// data.filter should come from Helpers/purchaseOrderFilters.buildPurchaseOrdersFilter.
export const LoadDashBoardOpenPOs = createAsyncThunk('order/openpoload', async (data) => {
  const params = {
    company_id: data.companyId || process.env.REACT_APP_COMP_EMM,
    $filter: data.filter,
    $count: true,
    $top: 0
  };
  const res = await API().get('/purchase_order_header', { params });
  return {
    count: res.data['@odata.count']
  };
});

export const LoadDashBoardFeedbackPOs = createAsyncThunk(
  'order/pendingfeedbacklinesload',
  async (data) => {
    const params = {
      company_id: data.companyId || process.env.REACT_APP_COMP_EMM,
      $filter: data.filter,
      $count: true,
      $top: 0
    };
    const res = await API().get('/purchase_order_lines', { params });
    return {
      count: res.data['@odata.count']
    };
  }
);

const initialState = {
  list: [],
  count: 0,
  currentOrder: {},
  orderLines: [],
  linesCount: 0,
  fetchingOrder: false,
  pendingPos: 0,
  pendingFeedbackLines: 0,
  openPoCount: 0
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    resetOrderSlice() {
      return initialState;
    },
    updateOrderLine(state, { payload }) {
      const { lineNo, targetField } = payload;
      const targetKey = Object.keys(targetField)[0];
      state.orderLines = state.orderLines.map((line) => {
        if (line.lineNo === lineNo) {
          line[targetKey] = targetField[targetKey];
          line.isEditing = true;
          if (
            !line.editedFields.includes(targetKey) &&
            targetKey !== 'validationErrors' &&
            targetKey !== 'validationWarnings' &&
            targetKey !== 'isValid'
          ) {
            line.editedFields.push(targetKey);
          }
        }
        return line;
      });
    },
    toggleSelectOrderLine(state, { payload }) {
      const { lineNo, documentNo, status } = payload;
      state.orderLines = state.orderLines.map((line) => {
        if (line.lineNo === lineNo && line.documentNo === documentNo) {
          line.isSelected = status;
        }
        return line;
      });
    },
    updateMultipleOrderLines(state, { payload }) {
      state.orderLines = state.orderLines.map((line) => {
        const payloadLine = payload.find(
          (l) => l.documentNo === line.documentNo && l.lineNo === line.lineNo
        );
        if (payloadLine) {
          return payloadLine;
        } else {
          return line;
        }
      });
    },
    handleFeedbackApprovalRejection(state, { payload }) {
      state.orderLines = state.orderLines.map((line) => {
        if (line.lineNo === payload.lineNo && line.documentNo === payload.documentNo) {
          line.feedbackStatus = payload.feedbackStatus;
        }
        return line;
      });
      if (payload.pathName === '/feedback-orders') {
        state.orderLines = state.orderLines.filter(
          (line) => line.lineNo !== payload.lineNo && line.documentNo !== payload.documentNo
        );
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(LoadOrders.pending, () => {
      return {
        ...initialState,
        fetchingOrder: true
      };
    });
    builder.addCase(LoadDashBoardPendingPOs.pending, (state) => {
      return {
        ...state,
        pendingPos: 0
      };
    });
    builder.addCase(LoadDashBoardPendingPOs.fulfilled, (state, { payload }) => {
      state.pendingPos = payload.count;
    });
    builder.addCase(LoadDashBoardPendingPOs.rejected, (state) => {
      return {
        ...state,
        pendingPos: 0
      };
    });
    builder.addCase(LoadDashBoardOpenPOs.pending, (state) => {
      return {
        ...state,
        openPoCount: 0
      };
    });
    builder.addCase(LoadDashBoardOpenPOs.fulfilled, (state, { payload }) => {
      state.openPoCount = payload.count;
    });
    builder.addCase(LoadDashBoardOpenPOs.rejected, (state) => {
      return {
        ...state,
        openPoCount: 0
      };
    });
    builder.addCase(LoadDashBoardFeedbackPOs.pending, (state) => {
      return {
        ...state,
        pendingFeedbackLines: 0
      };
    });
    builder.addCase(LoadDashBoardFeedbackPOs.fulfilled, (state, { payload }) => {
      state.pendingFeedbackLines = payload.count;
    });
    builder.addCase(LoadDashBoardFeedbackPOs.rejected, (state) => {
      return {
        ...state,
        pendingFeedbackLines: 0
      };
    });
    builder.addCase(LoadOrders.fulfilled, (state, { payload }) => {
      if (payload.type === 'list') {
        state.list = payload.orders.map((order) => {
          delete order['@odata.etag'];
          return order;
        });
        state.count = payload.count;
      } else {
        state.currentOrder = payload.orders.map((order) => {
          delete order['@odata.etag'];
          return order;
        })[0];
        state.count = 0;
      }
      state.fetchingOrder = false;
    });
    builder.addCase(LoadOrders.rejected, () => {
      return {
        ...initialState,
        fetchingOrder: false
      };
    });
    builder.addCase(LoadOrderLines.pending, (state) => {
      state.orderLines = [];
    });
    builder.addCase(LoadOrderLines.fulfilled, (state, { payload }) => {
      state.orderLines = payload.lines.map((line) => {
        delete line['@odata.etag'];
        line.isEditing = false;
        line.editedFields = [];
        line.isValid = true;
        line.validationErrors = [];
        line.validationWarnings = [];
        line.isSelected = false;
        return line;
      });
      state.linesCount = payload.count;
    });
    builder.addCase(LoadSearchedOrders.fulfilled, (state, { payload }) => {
      const searchedOrderes = payload.orders.map((order) => {
        delete order['@odata.etag'];
        return order;
      });
      const combineOrders = current(state).list.concat(searchedOrderes);
      state.list = combineOrders;
      state.list = _.uniqBy(combineOrders, 'docNo');
    });
    builder.addCase('auth/logout', () => {
      return initialState;
    });
  }
});

export default orderSlice.reducer;
export const orderAction = orderSlice.actions;
